import React, { useEffect, useState, useRef } from 'react';
import { List, ListItem, ListItemText, ListItemIcon, Collapse, Box, Select, MenuItem, Button, InputLabel, FormControl } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import reqProvider from '../../util/requests/reqProvider';
import Popup from './Popup';
import CustomDragPreview from './CustomDragPreview';
import { useNotify } from '../../util/hooks/notify/Notification';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import AddNewTreeNodePopup from './AddNewTreeNodePopup';
import CompanyOrBuSelector from '../../Components/CompanyOrBuSelector';


interface TreeNode {
  id: number;
  uuid: string;
  title: string;
  level: number;
  updatable: boolean;
  hasOpenShift: boolean | null;
  warehouseName: string | null;
  warehouseId: number | null;
  orderId: number | null;
  children: TreeNode[] | [];
  posId: number | null;
  key: number;
}

interface Company {
  id: number;
  name: string;
  posSetSelectorPrivileges: {
    createPrivilege: boolean | null;
    deletePrivilege: boolean | null;
    getPrivilege: boolean;
    getPrivilegeCompany: boolean;
    modifyPrivilege: boolean | null;
  };
}

const ItemType = 'TREE_NODE';

const DraggableTreeNode: React.FC<{ item: TreeNode; paddingLeft: number; moveNode: (draggedItem: TreeNode, targetItem: TreeNode) => void }> = ({ item, paddingLeft, moveNode }) => {
  const [open, setOpen] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const [activeItem, setActiveItem] = useState<TreeNode | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const getIconForLevel = (level: number) => {
    switch (level) {
      case 5:
        return <BusinessIcon sx={{ color: '#874B6D', marginRight: '8px' }} />;
      case 4:
        return <StoreIcon sx={{ color: '#874B6D', marginRight: '8px' }} />;
      case 3:
        return <StorefrontIcon sx={{ color: '#874B6D', marginRight: '8px' }} />;
      case 1:
        return <CreditCardIcon sx={{ color: '#874B6D', marginRight: '8px' }} />;
      case 2:
        return <LocalBarIcon sx={{ color: '#874B6D', marginRight: '8px' }} />;
      default:
        return null;
    }
  };

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemType,
    item: item,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemType,
    drop: (draggedItem: TreeNode, monitor) => {
      const didDrop = monitor.didDrop();
      if (didDrop) return;
      if (draggedItem.id !== item.id) {
        moveNode(draggedItem, item);
      }
    },
    canDrop: (draggedItem: TreeNode) => {
      const isCompany = (node: TreeNode) => node.level === 5;
      const isArea = (node: TreeNode) => node.level === 3;
      const isPOS = (node: TreeNode) => node.level === 1;

      if (draggedItem.hasOpenShift) return false;

      if (!item.updatable) return false;

      if (isCompany(draggedItem)) return false;

      if (isArea(item) && !isPOS(draggedItem)) return false;

      if (isPOS(draggedItem) && (isCompany(item) || isPOS(item))) return false;

      return draggedItem.level < item.level;

    },
  });

// cég alatt csak BU
  //BU alatt bármi
  //area alatt bármi kivéve BU
  //pult alatt pos
  // level 1 pos
  // level 2 pult
  // level 3 area
  // level 4 BU
  // level 5 company
  //level 7 blokknyomtató nem kell megjeleníteni


  useEffect(() => {
    preview(document.createElement('div'));
  }, [preview]);

  const handleClick = () => {
    setOpen(!open);
  };

  const handleIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setActiveItem(item);
    setOpenPopup((prevState) => !prevState);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
      setOpenPopup(false);
      setActiveItem(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={(node) => drag(drop(node))}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
      }}
    >
      <ListItem
        component="div"
        onClick={handleClick}
        sx={{
          paddingLeft,
          userSelect: 'none',
          position: 'relative',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            marginLeft: `${paddingLeft - 40}px`,
            width: paddingLeft > 0 ? '12px' : '0',
            height: '1px',
            backgroundColor: '#EDDFE3',
            display: paddingLeft > 0 ? 'block' : 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '0',
            marginLeft: `${paddingLeft - 40}px`,
            width: '1px',
            height: '100%',
            backgroundColor: '#EDDFE3',
            display: paddingLeft > 0 ? 'block' : 'none',
          },
          '&:last-child::after': {
            height: '50%',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 'auto', marginRight: '8px' }}>
          {hasChildren ? (open ? <RemoveIcon sx={{ color: '#874B6D' }} /> : <AddIcon sx={{ color: '#874B6D' }} />) : <FiberManualRecordIcon sx={{ fontSize: '10px', marginX: '7px' }} />}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: '#F9EAEF', borderRadius: '10px', padding: '8px', flexGrow: 1 }}>
              {getIconForLevel(item.level)}
              {item.title}
              <MoreVertIcon sx={{ marginLeft: 'auto', color: '#874B6D', cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(135, 75, 109, 0.1)', borderRadius: '50%' } }} onClick={handleIconClick} />
            </Box>
          }
        />
        {openPopup && activeItem && <Box sx={{ width: '60%', zIndex: 10, position: 'absolute', left: '90%', top: '50%' }} ref={popupRef}><Popup item={activeItem} /></Box>}
      </ListItem>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children?.map((child) => (
              <DraggableTreeNode key={child.id} item={child} paddingLeft={paddingLeft + 5} moveNode={moveNode} />
            ))}
          </List>
        </Collapse>
      )}
    </div>
  );
};

const Groups: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [data, setData] = useState<TreeNode[]>();
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const notify = useNotify()
  const [levelOneNodes, setLevelOneNodes] = useState<TreeNode[]>([]);
  const [openAddPopup, setOpenAddPopup] = useState(false);

  const onCloseAddPopup = () => {
    setOpenAddPopup(false);
  };

  const getCompany = async () => {
    const response = await reqProvider.get('group/companies');
    setSelectedCompany(response.data.list.find((company: { id: any; }) => company.id === response.data.defaultPosSetId))
    setCompanies(response.data.list);
  };

  const filterTreeNode = (node: TreeNode): TreeNode => {
    let filteredChildren = node.children;

    if (node.level === 5) {
      const levelOneChildren = node.children.filter((child) => child.level === 1);
      setLevelOneNodes(levelOneChildren);

      filteredChildren = node.children.filter((child) => child.level !== 1);
    }

    return {
      ...node,
      children: filteredChildren
        .filter((child) => child.level !== 7)
        .map((child) => filterTreeNode(child)),
    };
  };

  const getTree = async () => {
    const response = await reqProvider.get('group', { companyId: selectedCompany?.id });
    const filteredData = response.data.map((node: TreeNode) => filterTreeNode(node));
    setData(filteredData);
  };

  const moveNode = (draggedItem: TreeNode, targetItem: TreeNode) => {
    if (!data) return;

    const isBU = (node: TreeNode) => node.level === 4;
    const isCompany = (node: TreeNode) => node.level === 5;
    const isArea = (node: TreeNode) => node.level === 3;
    const isPOS = (node: TreeNode) => node.level === 1;

    if (draggedItem.hasOpenShift) {
      notify("Nem lehet mozgatni: aktív műszak van.", "error" );
      return;
    }

    if (!targetItem.updatable) {
      notify("Nincs jogosultságod ide áthelyezni.", "error" );
      return;
    }

    if (isCompany(targetItem) && !isBU(draggedItem)) {
      notify("Company alá csak BU-t lehet helyezni.", "error" );
      return;
    }

    if (isArea(targetItem) && isBU(draggedItem)) {
      notify("BU nem kerülhet Area alá.", "error" );
      return;
    }

    if (isPOS(draggedItem) && (isCompany(targetItem) || isPOS(targetItem))) {
      notify("POS csak Pult alá tehető.", "error" );
      return;
    }

    if (draggedItem.level > targetItem.level) {
      notify("Nem lehet magasabb szintű elemet alacsonyabb alá tenni.", "error" );
      return;
    }

    let draggedSubtree: TreeNode | null = null;

    const removeNode = (nodes: TreeNode[]): TreeNode[] => {
      return nodes
        .filter((node) => {
          if (node.id === draggedItem.id) {
            draggedSubtree = node;
            return false;
          }
          return true;
        })
        .map((node) => ({
          ...node,
          children: removeNode(node.children),
        }));
    };

    const addNode = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map((node) => {
        if (node.id === targetItem.id) {
          return {
            ...node,
            children: [...node.children, draggedSubtree!],
          };
        }
        return {
          ...node,
          children: addNode(node.children),
        };
      });
    };

    const updatedData = addNode(removeNode(data));
    setData(updatedData);
  };

  useEffect(() => {
    getCompany();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      getTree();
    }
  }, [selectedCompany]);

  return (
    <DndProvider backend={HTML5Backend}>
      <CustomDragPreview />
      <Box sx={{ padding: '20px', maxWidth: '500px', minWidth: '300px' }}>
        <Box sx={{display: 'flex' ,justifyContent: 'space-between'}}>
          <CompanyOrBuSelector
            menuItems={companies}
            label={"Válasszon céget"}
            value={selectedCompany || null}
            setValue={setSelectedCompany}
            posSet={'groupPosSetId'}
            />
          <Button sx={{ width: '80px', display: 'flex', alignItems: 'center', gap: '8px' }} variant={"contained"} onClick={()=>{setOpenAddPopup(true)}}> <AddIcon /> Új</Button>
        </Box>
        <List>
          {data?.map((rootItem) => (
            <DraggableTreeNode key={rootItem.id} item={rootItem} paddingLeft={0} moveNode={moveNode} />
          ))}
        </List>
      </Box>
      <AddNewTreeNodePopup open={openAddPopup} onClose={onCloseAddPopup} availablePOS={levelOneNodes.map(node => ({
        ...node,
        children: []
      }))} />
    </DndProvider>
  );
};

export default Groups;